import { useEffect, useRef } from 'react';
import { MChatMessage } from '@/src/modules/chat/models/chat-message';

interface IUseChatRoomScrollParams {
  scrollAreaRef: React.RefObject<HTMLDivElement | null>;
  onScrollToTop: () => void;
  isInitialLoading?: boolean;
  isLoadingMore?: boolean;
  isReachingEnd?: boolean;
  messages: MChatMessage[];
  loadMoreMessageThreshold?: number;
}

const BOTTOM_SCROLL_THRESHOLD = 50;

export function useChatRoomScroll({
  isInitialLoading,
  scrollAreaRef,
  onScrollToTop,
  isLoadingMore,
  isReachingEnd,
  messages,
  loadMoreMessageThreshold = 0,
}: IUseChatRoomScrollParams) {
  const lastScrollHeightRef = useRef<number>(0);

  const setCurrentLastScrollHeight = () => {
    if (!scrollAreaRef.current) {
      return;
    }
    lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
  };

  const scrollToBottomScrollArea = () => {
    scrollAreaRef.current?.scrollTo({
      top: scrollAreaRef.current.scrollHeight,
    });
    lastScrollHeightRef.current = scrollAreaRef.current?.scrollHeight || 0;
  };

  const handleScrollReachTop = () => {
    if (!scrollAreaRef.current) {
      return;
    }

    if (
      scrollAreaRef.current.scrollTop <= loadMoreMessageThreshold &&
      !isInitialLoading &&
      !isLoadingMore &&
      !isReachingEnd
    ) {
      lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
      onScrollToTop();
    }
  };

  useEffect(() => {
    if (!scrollAreaRef.current || isInitialLoading) {
      return;
    }
    const scrollToBottomTimeout = setTimeout(
      () => scrollToBottomScrollArea(),
      100
    );
    return () => {
      clearTimeout(scrollToBottomTimeout);
    };
  }, [scrollAreaRef.current, isInitialLoading]);

  useEffect(() => {
    if (!scrollAreaRef.current || isInitialLoading || isLoadingMore) {
      return;
    }

    // Only scroll to bottom on new message if the user is already at the bottom
    // before the new message is added, otherwise do not scroll to bottom
    const scrollPositionReachedBottom =
      scrollAreaRef.current.scrollTop + scrollAreaRef.current.clientHeight >=
      scrollAreaRef.current.scrollHeight -
        scrollAreaRef.current.offsetHeight -
        BOTTOM_SCROLL_THRESHOLD;
    if (scrollPositionReachedBottom) {
      scrollToBottomScrollArea();
    }

    if (scrollAreaRef.current.scrollTop <= loadMoreMessageThreshold) {
      const scrollHeightDifference =
        scrollAreaRef.current.scrollHeight - lastScrollHeightRef.current;
      scrollAreaRef.current.scrollTop = scrollHeightDifference;
      lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  return {
    scrollToBottomScrollArea,
    handleScrollReachTop,
    setCurrentLastScrollHeight,
  };
}
